import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase, getUserProfile, signOut } from '../lib/supabase';
import { UserChip } from './UserChip';
import { MobileAccountSheet } from './MobileAccountSheet';
import {
  Home, GitBranch, CreditCard, TrendingUp, AlertTriangle, Settings,
  UserPlus, LogOut, User, LayoutDashboard, Plug, Globe, MessageCircle, ShieldAlert, Activity
} from 'lucide-react';
import '../styles/instacut.css';

const NAV = [
  { to: '/admin/dashboard',  label: 'Dashboard',   icon: LayoutDashboard, group: 'Platform' },
  { to: '/admin/shops',      label: 'Shops',       icon: Home,           group: 'Platform' },
  { to: '/admin/onboard',    label: 'Onboard shop',icon: UserPlus,       group: 'Platform' },
  { to: '/admin/onboarding', label: 'Onboarding',  icon: GitBranch,      group: 'Platform' },
  { to: '/admin/billing',    label: 'Billing',     icon: CreditCard,     group: 'Platform' },
  { to: '/admin/revenue',    label: 'Revenue',     icon: TrendingUp,     group: 'Platform' },
  { to: '/admin/churn',        label: 'Churn watch',  icon: AlertTriangle,  group: 'Platform' },
  { to: '/admin/integrations', label: 'Integrations', icon: Plug,           group: 'Platform' },
  { to: '/admin/message-templates', label: 'Message templates', icon: MessageCircle, group: 'Platform' },
  { to: '/admin/booking-intel',   label: 'Booking intel',    icon: ShieldAlert, group: 'Platform' },
  { to: '/admin/system-health',   label: 'System health',    icon: Activity,    group: 'Platform' },
  { to: '/admin/domain-readiness', label: 'Domain readiness', icon: Globe,  group: 'Platform' },
  { to: '/admin/settings',     label: 'Settings',     icon: Settings,       group: 'Platform' },
];

export const AdminShellContext = React.createContext({ profile: null });
export const useAdminShell = () => React.useContext(AdminShellContext);

export const AdminShell = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [sessionEmail, setSessionEmail] = useState('');
  const [checking, setChecking] = useState(true);
  const [denied, setDenied] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!supabase) { navigate('/signin'); return; }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/signin'); return; }
      const { data } = await getUserProfile(session.user.id);
      if (!mounted) return;
      if (!data || data.role !== 'master_admin') {
        setDenied(true);
        setChecking(false);
        return;
      }
      setProfile(data);
      setSessionEmail(session.user.email || '');
      setChecking(false);
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  // M2 — same scroll-end affordance toggle as AppShell.
  useEffect(() => {
    const el = document.querySelector('aside.side');
    if (!el) return;
    const onScroll = () => {
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      el.classList.toggle('scrolled-end', atEnd);
    };
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [profile]);

  if (checking) {
    return (
      <div className="app-root" data-testid="adminshell-loading">
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div>
      </div>
    );
  }

  if (denied) {
    return (
      <div className="app-root" data-testid="admin-denied">
        <div className="statebox" style={{ margin: 60 }}>
          <h5>Access denied</h5>
          <p>The platform control panel is for master admins only.</p>
          <Link to="/dashboard" className="btn s" style={{ marginTop: 10 }}>Back to your dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="app-root">
      <div className="app">
        <aside className="side" data-testid="admin-sidebar">
          <Link to="/admin/shops" className="brandrow">
            <img className="mark" src="/logos/instacut-mark-26.svg" alt="" width="26" height="26" />
            Instacut
          </Link>
          <div className="navg">Platform</div>
          {NAV.map(item => {
            const Icon = item.icon;
            // Exact match wins; prefix-match only for /admin/shops (so shop-detail
            // pages still highlight Shops). Ensures /admin/onboard doesn't also
            // highlight when the user is on /admin/onboarding and vice-versa.
            const active = location.pathname === item.to ||
              (item.to === '/admin/shops' && location.pathname.startsWith('/admin/shops/'));
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`nav ${active ? 'on' : ''}`}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="i" size={17} />
                {item.label}
              </Link>
            );
          })}
          <UserChip profile={profile} email={sessionEmail} />
          <Link
            to="/account"
            className="signout"
            style={{ background: 'transparent', color: 'var(--mute)', marginBottom: 4 }}
            data-testid="admin-nav-account"
          >
            <User size={15} /> My account
          </Link>
          <button className="signout" onClick={handleSignOut} data-testid="admin-signout">
            <LogOut size={15} /> Sign out
          </button>

          {/* Mobile-only — see AppShell.js for the same treatment. */}
          <MobileAccountSheet
            profile={profile}
            email={sessionEmail}
            onSignOut={handleSignOut}
            active={mobileMenuOpen}
            onOpen={() => setMobileMenuOpen(true)}
            onClose={() => setMobileMenuOpen(false)}
            testIdPrefix="admin-mobile-account"
          />
        </aside>

        <AdminShellContext.Provider value={{ profile }}>
          {children}
        </AdminShellContext.Provider>
      </div>
    </div>
  );
};
